import { defineNuxtPlugin } from '#app'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { useAuthStore } from '~/stores/authStore'
import { useLogger } from '../composables/useLogger'

export default defineNuxtPlugin(async (nuxtApp) => {
  const logger = useLogger()
  if (Capacitor.isNativePlatform()) {
    logger.debug('Setting up deep link listeners')
    
    const handleDeepLink = (urlString: string) => {
      try {
        logger.debug('Processing deep link', { url: urlString })
        const url = new URL(urlString)
        
        const queryParams: Record<string, string> = {}
        
        // Extract hash fragment if present and parse it
        const hash = url.hash ? url.hash.substring(1) : ''
        if (hash && hash.includes('=')) {
          const hashParams = new URLSearchParams(hash)
          hashParams.forEach((value, key) => {
            queryParams[key] = value
          })
          logger.debug('Deep link: Extracted params from hash', { 
            paramCount: Object.keys(queryParams).length 
          })
        }

        // Also get any query params from the URL
        const urlParams = new URLSearchParams(url.search)
        urlParams.forEach((value, key) => {
          queryParams[key] = value
        })

        const router = nuxtApp.$router
        
        // Handle different paths
        switch (url.pathname) {
          case '/password-reset-confirm':
            logger.debug('Handling password reset deep link', { queryParams })
            router.push({
              path: '/password-reset-confirm',
              query: queryParams  // Pass all params from both hash and query
            })
            break
            
          case '/auth/callback':
            logger.debug('Handling auth callback deep link')
            router.push({
              path: '/auth/callback',
              query: queryParams
            })
            break

          case '/auth/callback-invite':
            logger.debug('Handling landing invite deep link', { queryParams })
            router.push({
              path: '/auth/callback-invite',
              query: queryParams
            })
            break

          case '/landings/landonhome':
            logger.debug('Handling landing on home deep link', { queryParams })
            router.push({
              path: '/',
              query: queryParams
            })
            break

          case '/test/test-deep-link':
            logger.debug('Handling test deep link', { queryParams })
            router.push({
              path: '/test/test-deep-link',
              query: queryParams
            })
            break
            
          default:
            logger.warn('Unhandled deep link path', { pathname: url.pathname })
        }
      } catch (error) {
        logger.logErrorCaught('Error handling deep link', 'DeepLinks.client.ts', error, { url: urlString })
      }
    }

    try {
      // Register listener before checking initial URL
      await App.addListener('appUrlOpen', (event: { url: string }) => {
        logger.debug('Deep link received in listener', { url: event.url })
        handleDeepLink(event.url)
      })

      // Check for initial URL (app launched via deep link)
      const { url } = await App.getLaunchUrl() || { url: null }
      if (url) {
        logger.debug('Initial deep link URL in plugin', { url })
        handleDeepLink(url)
      }
    } catch (error) {
      logger.logErrorCaught('Error setting up deep link handlers', 'DeepLinks.client.ts', error)
    }
  }

  // Return empty object to satisfy plugin interface
  return {}
}) 